/**
 * A field value in the words the patient page uses for it — « Oui », a formatted date, a rounded
 * number, a list joined with commas.
 *
 * Its own module rather than a second export on [components/ValueDisplayNotArray], which is where
 * it was written: the value a tooltip names must be named the way the line beside it names it, so
 * the badge and the row have to share one formatter — and a component file that also exports a
 * function loses Fast Refresh for the whole file.
 */
import dayjs from "dayjs";
import { msg } from "@lingui/macro";
import { MageXPatient } from "@prisma/client";
import type { TypeEnum } from "@api/src/types/few-schot-schema";
import { i18n } from "@app/i18n";

// Module-level descriptors so Lingui's extractor picks the source strings up.
// Resolved at runtime via ``i18n._`` — needed because ``formatValue`` is a
// plain function (not a component) and ``t`` macros pulled from a parameter
// don't get transformed (see note inside ``formatValue``).
const MSG_YES = msg`Oui`;
const MSG_NO = msg`Non`;
const MSG_MAN = msg`Homme`;
const MSG_WOMAN = msg`Femme`;

// Patient truth values can sometimes be persisted with a stray prefix token
// (e.g. "kshndzd 27/05/1990"). For UI rendering, extract the actual date/number.
const extractDateFromString = (raw: string): string | null => {
  const isoMatch = raw.match(/\d{4}-\d{2}-\d{2}/);
  if (isoMatch) return isoMatch[0];

  const dmySlashMatch = raw.match(/\d{2}\/\d{2}\/\d{4}/);
  if (dmySlashMatch) return dmySlashMatch[0];

  const dmyDashMatch = raw.match(/\d{2}-\d{2}-\d{4}/);
  if (dmyDashMatch) return dmyDashMatch[0].replace(/-/g, "/");

  return null;
};

const formatExtractedDate = (dateStr: string): string | null => {
  const isoMatch = dateStr.match(/\d{4}-\d{2}-\d{2}/);
  if (isoMatch) {
    const formatted = dayjs(isoMatch[0]).format("DD/MM/YYYY");
    return formatted === "Invalid Date" ? null : formatted;
  }

  const dmyMatch = dateStr.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (dmyMatch) {
    const [, dd, mm, yyyy] = dmyMatch;
    const formatted = dayjs(`${yyyy}-${mm}-${dd}`).format("DD/MM/YYYY");
    return formatted === "Invalid Date" ? null : formatted;
  }

  const formatted = dayjs(dateStr).format("DD/MM/YYYY");
  return formatted === "Invalid Date" ? null : formatted;
};

const extractStandaloneNumber = (raw: string): number | null => {
  const matches = raw.match(/(?<![A-Za-z])-?\d+(?:[.,]\d+)?(?![A-Za-z])/g);
  if (!matches || matches.length === 0) return null;
  const parsed = parseFloat(matches[matches.length - 1].replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

// A field value in the words the patient page uses for it — « Oui », a formatted date, a rounded
// number. Exported because a tooltip that names the value a clinician ruled on must name it the
// way the line beside it does; two formatters would eventually disagree, and a badge quoting
// ``false`` next to a line reading « Non » is exactly the confusion it is there to remove.
export const formatValue = (
  value: unknown,
  property: keyof MageXPatient,
  type?: TypeEnum,
  debug?: string,
): string | null => {
  if (debug) {
    console.warn("value", property, type, value, debug);
  }
  // if (value === null || value === undefined) return "N/A";
  if (value == null || value == undefined) return null;

  // Always render native booleans as Oui/Non, regardless of the `type` prop.
  // This guards against cases where the actual DB value is boolean but the
  // schema type was not propagated correctly to this component.
  if (typeof value === "boolean") {
    return value ? i18n._(MSG_YES) : i18n._(MSG_NO);
  }

  // A scalar-typed field can still hold SEVERAL values: the care pathway writes one entry per
  // reality a moment establishes, so a record naming five treatments reaches a field the ontology
  // types as one string as a list of five. Each item is formatted the way one value would be, and
  // they are joined — ``toString()`` produced something similar by accident, and on a ``number``
  // field it produced nothing at all: the number reader calls ``.match`` on what it is handed, an
  // array has none, and the exception blanked the whole record. The array types have their own
  // renderers and never reach this one.
  if (
    Array.isArray(value) &&
    type !== "array_enum" &&
    type !== "array_string" &&
    type !== "scores"
  ) {
    const parts = value
      .map((item) => formatValue(item, property, type, debug))
      .filter((part): part is string => Boolean(part?.trim()));
    return parts.length > 0 ? parts.join(", ") : null;
  }

  if (property === "sexe") {
    if ((value as string).toLocaleLowerCase().includes("h")) {
      return i18n._(MSG_MAN);
    }
    if ((value as string).toLocaleLowerCase().includes("f")) {
      return i18n._(MSG_WOMAN);
    }
    return null;
  }

  // Field-specific cleanups for cases where the persisted value includes prefixes.
  if (property === "date_naissance" && typeof value === "string") {
    const extracted = extractDateFromString(value);
    if (extracted) {
      return formatExtractedDate(extracted);
    }
  }

  if (property === "taille" && typeof value === "string") {
    const num = extractStandaloneNumber(value);
    if (num !== null) return num.toFixed(0);
  }

  switch (type) {
    case "boolean":
      return value ? i18n._(MSG_YES) : i18n._(MSG_NO);
    case "number": {
      const num =
        typeof value === "number"
          ? value
          : (() => {
              const extracted = extractStandaloneNumber(value as string);
              if (extracted === null) return NaN;
              return extracted;
            })();
      if (isNaN(num)) return null;
      return num.toFixed(0);
    }
    case "date":
      if (typeof value === "string") {
        const extracted = extractDateFromString(value);
        if (extracted) return formatExtractedDate(extracted);

        const formatted = dayjs(value).format("DD/MM/YYYY");
        return formatted === "Invalid Date" ? null : formatted;
      }
      return value.toString();
    case "scores":
    case "array_enum":
    case "array_string":
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return null;
        }
      }
      return JSON.stringify(value, null, 2);
    default:
      return value.toString();
  }
};
